import React, { forwardRef, PropsWithChildren, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useMergeRefs } from 'use-callback-ref';
import { isOverflowActive } from './isOverflowActive';
import { OverflowContext } from './OverflowContext';

const Root = styled.span.attrs({ 'data-test': 'text-content' })`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

/**
 * It adds default ellipsis styling
 * It gives tooltip a possibility to check if overflow happens inside any nested children
 */
export const OverflowTrigger = forwardRef<HTMLSpanElement, PropsWithChildren<unknown>>(({ children, ...rest }, externalRef?) => {
  const overflowContextSet = useContext(OverflowContext);
  const ref = useRef<HTMLSpanElement>(null);

  const mergedRefs = useMergeRefs([ref, externalRef]);

  if (!overflowContextSet) {
    throw new Error('OverflowTrigger should be placed inside Tooltip.');
  }

  useEffect(() => {
    const isCurrentOverflowActive = () => Boolean(ref.current && isOverflowActive(ref?.current));
    overflowContextSet.add(isCurrentOverflowActive);
    return () => {
      overflowContextSet.delete(isCurrentOverflowActive);
    };
  }, [overflowContextSet, ref]);

  return (
    <Root {...rest} ref={mergedRefs}>
      {children}
    </Root>
  );
});
